import { HelpCircle, AlertCircle, FileText, X } from 'lucide-react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  useUploadInvoiceValidationFileMutation,
  useRunInvoiceGroupValidationMutation,
  useDeleteInvoiceValidationFileMutation,
  useGetCsvValidateSourcesQuery,
} from 'src/redux/billing/billing.endpoints';
import { InvoiceGroupDetails, ValidationFile } from 'src/redux/billing/billing.types';
import StyledDropdown from 'src/components/styled-dropdown';
import { Button } from 'src/components/ui/button';

interface PendingFile {
  file: File | null;
  source: string;
}

interface ValidateUploadContainerProps {
  invoiceGroupId: string;
  invoiceGroupDetails?: InvoiceGroupDetails | null;
  onUpload?: () => void;
  onValidate?: () => void;
}

const FILE_TYPES = ['.csv'];
const MAX_SIZE_BYTES = 25 * 1024 * 1024;

const formatUploadedAt = (iso: string) => {
  try {
    const d = new Date(iso);
    return d.toLocaleString();
  } catch {
    return iso;
  }
};

const ValidateUploadContainer: React.FC<ValidateUploadContainerProps> = ({
  invoiceGroupId,
  invoiceGroupDetails,
  onUpload,
  onValidate,
}) => {
  const [pendingRows, setPendingRows] = useState<PendingFile[]>([{ file: null, source: '' }]);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [fileInputKey, setFileInputKey] = useState(0);

  const { data: sources = [], isLoading: isLoadingSources } = useGetCsvValidateSourcesQuery();
  const sourceOptions = useMemo(
    () => sources.map((s) => ({ value: s, label: s })),
    [sources]
  );
  const [uploadFile, { isLoading: isUploading }] = useUploadInvoiceValidationFileMutation();
  const [runValidation, { isLoading: isRunningValidation }] = useRunInvoiceGroupValidationMutation();
  const [deleteValidationFile, { isLoading: isDeletingValidationFile }] =
    useDeleteInvoiceValidationFileMutation();

  const validationFiles: ValidationFile[] = invoiceGroupDetails?.validations?.files ?? [];
  const validationErrors: string[] = (invoiceGroupDetails?.errors ?? []).map((e) => e.error);
  const hasUploadedFiles = validationFiles.length > 0;

  /** Keep the native file input in sync with pending state where the browser allows it. */
  const fileInputRefs = useRef<(HTMLInputElement | null)[]>([]);
  useEffect(() => {
    pendingRows.forEach((row, index) => {
      const input = fileInputRefs.current[index];
      if (!input) {
        return;
      }
      if (!row.file) {
        input.value = '';
        return;
      }
      try {
        const dt = new DataTransfer();
        dt.items.add(row.file);
        input.files = dt.files;
      } catch {
        // Some browsers restrict programmatic FileList assignment; filename still shown beside control.
      }
    });
  }, [pendingRows, fileInputKey]);

  const setRowFile = (index: number, file: File | null) => {
    setUploadError(null);
    setPendingRows((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], file };
      return next;
    });
  };

  const setRowSource = (index: number, source: string) => {
    setUploadError(null);
    setPendingRows((prev) => {
      const next = [...prev];
      next[index] = {
        ...next[index],
        source,
        ...(source.trim() ? {} : { file: null as File | null }),
      };
      return next;
    });
    if (!source.trim()) {
      setFileInputKey((k) => k + 1);
    }
  };

  const onFileInputChange = (index: number, e: React.ChangeEvent<HTMLInputElement>, source: string) => {
    if (!source.trim()) {
      setUploadError('Select a source before choosing a file.');
      e.target.value = '';
      return;
    }
    const f = e.target.files?.[0] ?? null;
    e.target.value = '';
    if (!f) {
      setRowFile(index, null);
      return;
    }
    const name = f.name.toLowerCase();
    const isCsv = FILE_TYPES.some((type) => name.endsWith(type));
    if (!isCsv) {
      setUploadError(`Please select a ${FILE_TYPES.join(' or ')} file.`);
      setRowFile(index, null);
      return;
    }
    if (f.size > MAX_SIZE_BYTES) {
      setUploadError('File too large. Max 25 MB.');
      setRowFile(index, null);
      return;
    }
    setUploadError(null);
    setRowFile(index, f);
  };

  const uploadOne = async(index: number) => {
    const row = pendingRows[index];
    if (!row.file || !row.source) {
      setUploadError('Select a file and a source for this row.');
      return;
    }
    setUploadError(null);
    try {
      await uploadFile({ file: row.file, id: invoiceGroupId, source: row.source }).unwrap();
      setRowFile(index, null);
      setRowSource(index, '');
      setFileInputKey((k) => k + 1);
      onUpload?.();
    } catch (err: unknown) {
      const message = err && typeof err === 'object' && 'data' in err
        ? String((err as { data?: unknown }).data)
        : 'Upload failed.';
      setUploadError(message);
    }
  };

  const handleRunValidation = async() => {
    setUploadError(null);
    try {
      await runValidation(invoiceGroupId).unwrap();
      onValidate?.();
    } catch (err: unknown) {
      const message = err && typeof err === 'object' && 'data' in err
        ? String((err as { data?: unknown }).data)
        : 'Validation failed.';
      setUploadError(message);
    }
  };

  const handleRemoveValidationFile = async(source: string, filename: string) => {
    setUploadError(null);
    try {
      await deleteValidationFile({ id: invoiceGroupId, source, filename }).unwrap();
      onUpload?.();
    } catch (err: unknown) {
      const message = err && typeof err === 'object' && 'data' in err
        ? String((err as { data?: unknown }).data)
        : 'Could not remove file.';
      setUploadError(message);
    }
  };

  return (
    <div className="p-4 pt-2 bg-gray-201 rounded-b-lg">
      <div className="flex flex-row gap-2 items-start mb-3">
        <HelpCircle className="h-4 w-4 shrink-0 mt-0.5" />
        <p className="text-xs text-gray-600">
          Select a source, choose a CSV file, then click Upload. When you are ready, click Run validation.
        </p>
      </div>

      {/* Pending uploads: single file + source row; clears after upload */}
      <div className="space-y-2 mb-4">
        {pendingRows.map((row, index) => {
          const fileInputId = `validate-invoice-csv-${index}-${fileInputKey}`;
          const hasSource = Boolean(row.source?.trim());
          const hasFile = Boolean(row.file);
          const canUploadRow = hasSource && hasFile && !isUploading;
          return (
            <div
              key={`${index}-${fileInputKey}`}
              className="flex flex-wrap items-center gap-2"
            >
              <StyledDropdown
                options={sourceOptions}
                selectedValue={row.source?.trim() ? row.source : undefined}
                onSelect={(value) => setRowSource(index, value)}
                placeholder="Source"
                buttonVariant="primary"
                isLoading={isLoadingSources}
                buttonClassName="rounded-full bg-white border border-gray-200 text-gray-700 h-8 min-w-[160px] text-sm justify-between px-3 py-0 font-normal hover:bg-gray-50"
                className="shrink-0"
              />
              <input
                id={fileInputId}
                key={fileInputKey}
                ref={(el) => {
                  fileInputRefs.current[index] = el;
                }}
                type="file"
                accept=".csv,text/csv"
                disabled={!hasSource}
                className="sr-only"
                onChange={(e) => onFileInputChange(index, e, row.source)}
              />
              <label
                htmlFor={hasSource ? fileInputId : undefined}
                title={hasSource ? undefined : 'Select a source first'}
                className={`inline-flex h-8 min-w-0 max-w-[min(100%,320px)] items-center rounded-full border border-gray-200 bg-white px-4 text-sm text-gray-700 transition-colors ${
                  hasSource
                    ? 'cursor-pointer hover:bg-gray-50'
                    : 'cursor-not-allowed opacity-50 pointer-events-none'
                }`}
              >
                <span className="truncate">
                  {row.file ? row.file.name : 'Choose file'}
                </span>
              </label>
              <Button
                type="button"
                size="sm"
                variant="black"
                className="h-8 text-xs font-medium text-white hover:bg-gray-900"
                onClick={() => uploadOne(index)}
                disabled={!canUploadRow}
              >
                {isUploading ? 'Uploading…' : 'Upload'}
              </Button>
            </div>
          );
        })}
      </div>

      {uploadError && (
        <div className="flex items-center gap-2 mb-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          <AlertCircle className="h-4 w-4 shrink-0" />
          {uploadError}
        </div>
      )}

      {/* Uploaded files: show each file as a record */}
      {validationFiles.length > 0 && (
        <div className="mb-4">
          <h4 className="text-xs font-medium text-gray-700 mb-2 flex items-center gap-1">
            <FileText className="h-3.5 w-3.5" />
            Uploaded files
          </h4>
          <div className="flex flex-wrap gap-2">
            {validationFiles.map((f, i) => {
              const fileSource = f.source ?? f.data?.[0]?.source;
              const titleParts = [
                fileSource ? `Source: ${fileSource}` : null,
                f.uploaded_at ? formatUploadedAt(f.uploaded_at) : null,
              ].filter(Boolean);
              return (
                <div
                  key={`${f.filename}-${i}`}
                  className="inline-flex w-fit max-w-full items-center gap-1.5 rounded-full border border-gray-200 bg-white py-0.5 pl-3 pr-0.5 text-xs text-gray-800 shadow-sm"
                  title={titleParts.length > 0 ? titleParts.join(' · ') : undefined}
                >
                  {fileSource ? (
                    <span className="max-w-[100px] shrink-0 truncate rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium text-gray-700">
                      {fileSource.replace(/_/g, ' ')}
                    </span>
                  ) : null}
                  <span className="max-w-[min(200px,65vw)] truncate">
                    {f.filename}
                  </span>
                  <button
                    type="button"
                    className="inline-flex shrink-0 rounded-full p-1.5 text-gray-600 hover:bg-gray-200 hover:text-gray-900 disabled:opacity-40 disabled:hover:bg-transparent"
                    aria-label={`Remove ${f.filename}`}
                    title={fileSource ? undefined : 'Source is required to remove this file'}
                    disabled={isDeletingValidationFile || !fileSource}
                    onClick={() => {
                      if (!fileSource) {
                        return;
                      }
                      handleRemoveValidationFile(fileSource, f.filename);
                    }}
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Validation errors (from invoice group top-level errors) */}
      {validationErrors.length > 0 && (
        <div className="mb-4">
          <h4 className="text-xs font-medium text-red-700 mb-2 flex items-center gap-1">
            <AlertCircle className="h-3.5 w-3.5" />
            Validation errors
          </h4>
          <ul className="text-xs text-red-600 space-y-1 list-disc list-inside max-h-32 overflow-y-auto">
            {validationErrors.map((msg, i) => (
              <li key={i}>
                {msg}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Run validation button */}
      <div className="pt-2 border-t border-gray-200">
        <Button
          type="button"
          size="sm"
          variant="black"
          className="text-white hover:bg-gray-900"
          onClick={handleRunValidation}
          disabled={!hasUploadedFiles || isRunningValidation}
        >
          {isRunningValidation ? 'Running validation…' : 'Run validation'}
        </Button>
        {!hasUploadedFiles && (
          <span className="ml-2 text-xs text-gray-500">
            Upload at least one file to run validation.
          </span>
        )}
      </div>
    </div>
  );
};

export default ValidateUploadContainer;
